import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api, fileUrl, thumbUrl, videoUrl, posterUrl, resolveAvatar } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { EyeOff, Loader2, Star, Images, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const Blind = () => {
  const { user, loading: authLoading, login, requireAction, openPersona } = useAuth();
  const [needsPersona, setNeedsPersona] = useState(false);
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState(false);
  const [myVote, setMyVote] = useState(0);
  const [hover, setHover] = useState(0);
  const [voting, setVoting] = useState(false);
  const [count, setCount] = useState(0);

  const loadNext = useCallback(async () => {
    setLoading(true); setRevealed(false); setMyVote(0); setHover(0);
    try { const r = await api.get("/blind/next"); setItem(r.data); setNeedsPersona(false); }
    catch (e) { setItem(null); setNeedsPersona(e?.response?.status === 403); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setLoading(false); return; }
    loadNext();
  }, [user, authLoading, loadNext]);

  const vote = async (v) => {
    if (!requireAction() || voting) return;
    setVoting(true);
    try {
      const r = await api.post(`/media/${item.id}/rate`, { value: v });  // the ONE canonical 1–10 rating system
      setMyVote(v);
      // Reveal: the blind payload had no title/creator/score — fetch the normal record now that the vote is in
      let full = {};
      try { full = (await api.get(`/media/${item.id}`)).data || {}; } catch {}
      setItem((it) => ({ ...it, ...full, avg_rating: r.data.avg_rating, rating_count: r.data.rating_count }));
      setRevealed(true);
      setCount((c) => c + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Vote failed"); }
    finally { setVoting(false); }
  };

  if (authLoading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!user) return (
    <div className="min-h-screen relative z-10"><Navbar />
      <div className="max-w-lg mx-auto text-center py-32 px-6">
        <EyeOff className="w-10 h-10 mx-auto text-primary mb-4" />
        <h1 className="font-heading text-3xl mb-3">Blind Rating is for members</h1>
        <p className="text-muted-foreground mb-6">Rate feet with zero bias — no names, no scores until you vote.</p>
        <Button data-testid="blind-signin" onClick={login} className="rounded-full">Sign in</Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen relative z-10">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-8 py-8">
        <div className="flex items-center gap-2 text-primary mb-1"><EyeOff className="w-4 h-4" /><span className="text-xs tracking-[0.2em] uppercase font-bold">Blind Rating</span></div>
        <div className="flex items-end justify-between gap-4">
          <div>
            <h1 data-testid="blind-title" className="font-heading text-4xl sm:text-5xl font-light tracking-tighter">Blind Rating</h1>
            <p data-testid="blind-helper" className="text-muted-foreground mt-2"><span className="text-foreground font-medium">Rate without bias.</span> No creator, no title, no score until you've voted — just your honest 1–10. {count > 0 && <span className="text-primary font-medium">{count} rated</span>}</p>
          </div>
        </div>

        {loading ? <div className="h-[420px] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
          : !item ? (
            <div data-testid="blind-empty" className="h-[420px] flex flex-col items-center justify-center gap-3 text-muted-foreground text-center px-4">
              {needsPersona ? <><p>Finish your persona to start rating blind.</p><Button data-testid="blind-persona" onClick={openPersona} className="rounded-full">Complete persona</Button></> : "Nothing left to rate right now."}
            </div>)
          : (
          <div className="mt-6 rounded-2xl border border-border bg-card overflow-hidden" data-testid="blind-card">
            <div className="relative aspect-[4/3] bg-black">
              {item.media_type === "video" ? <video src={videoUrl(item)} poster={posterUrl(item, 1200)} muted loop autoPlay playsInline className="w-full h-full object-cover" />
                : <img src={thumbUrl(item.file_ref, 1200)} alt="Blind item" className="w-full h-full object-cover" />}
              {!revealed && <span className="absolute top-3 left-3 flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full bg-black/70 border border-white/10"><EyeOff className="w-3 h-3" /> Creator hidden</span>}
            </div>

            <div className="p-5">
              {!revealed ? (
                <>
                  <div className="text-xs tracking-widest uppercase text-muted-foreground mb-3 text-center">Tap your rating (1–10)</div>
                  <div className="flex justify-center gap-1 flex-wrap">
                    {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                      <button key={n} data-testid={`blind-vote-${n}`} disabled={voting}
                        onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => vote(n)}
                        className={cn("w-9 h-11 rounded-lg border flex items-center justify-center font-mono text-sm transition-all",
                          (hover >= n) ? "border-primary bg-primary/15 text-primary -translate-y-0.5" : "border-border text-muted-foreground hover:text-foreground")}>
                        {n}
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div data-testid="blind-reveal">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Star className="w-6 h-6 fill-primary text-primary" />
                    <span className="font-mono text-2xl font-semibold">{item.avg_rating}</span>
                    <span className="text-muted-foreground text-sm">/10 · {item.rating_count} votes · you gave {myVote}</span>
                  </div>
                  <div className="rounded-xl border border-border bg-secondary/30 p-4 flex items-center gap-3">
                    <button onClick={() => navigate(`/u/${item.uploader_id}`)} className="flex items-center gap-3 min-w-0 flex-1 group">
                      <Avatar className="w-10 h-10 border border-border"><AvatarImage src={resolveAvatar(item.uploader_picture)} /><AvatarFallback className="bg-secondary text-xs">{item.uploader_name?.[0]}</AvatarFallback></Avatar>
                      <div className="min-w-0 text-left">
                        <div className="text-sm font-medium truncate group-hover:text-primary transition-colors">{item.title}</div>
                        <div className="text-xs text-muted-foreground truncate">@{item.uploader_name}{item.location?.display ? ` · ${item.location.display}` : ""}</div>
                      </div>
                    </button>
                    <Button data-testid="blind-view-gallery" onClick={() => navigate(`/u/${item.uploader_id}`)} variant="outline" size="sm" className="rounded-full border-border shrink-0"><Images className="w-4 h-4 mr-1.5" />Gallery</Button>
                  </div>
                  <Button data-testid="blind-next" onClick={loadNext} className="w-full mt-4 rounded-full">Next <ArrowRight className="w-4 h-4 ml-1" /></Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Blind;
